import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { useIsKioskOwner } from '../../hooks/useCurrentRole';
import { FaFilter } from 'react-icons/fa';
import { HiChevronDown } from 'react-icons/hi';

interface PageLayoutProps {
  /**
   * Page title displayed in the header
   */
  title: string;
  
  /**
   * Optional secondary text displayed below the title
   */
  subtitle?: string;
  
  /**
   * Optional action buttons to display in the header
   */
  actions?: ReactNode;
  
  /**
   * Optional search/filter components to display below the header
   */
  filters?: ReactNode;
  
  /**
   * Main content of the page
   */
  children: ReactNode;
  
  /**
   * Optional footer content (like pagination)
   */
  footer?: ReactNode;
  
  /**
   * Optional additional CSS classes
   */
  className?: string;

  /**
   * Stretch the content card to the full viewport height (for editor-style screens)
   */
  fillHeight?: boolean;
}

/**
 * Standardized page layout component that ensures consistent structure across all pages.
 * Follows the design system specifications for spacing, typography, and colors.
 */
export const PageLayout = ({
  title,
  subtitle,
  actions,
  filters,
  children,
  footer,
  className,
  fillHeight = false,
}: PageLayoutProps): React.ReactElement => {
  const { pathname } = useLocation();
  const isKioskOwner = useIsKioskOwner();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  return (
    <div className={classNames(isKioskOwner ? 'owner-mobile-page min-h-full p-4 text-gray-900 dark:text-gray-100 sm:p-6' : 'min-h-full p-6 text-gray-900 dark:text-gray-100', fillHeight && 'flex h-full flex-col', className)}>
      <div className={classNames(
        'sticky top-0 z-20 shrink-0 mb-6 border-b border-white/40 bg-[linear-gradient(180deg,rgba(248,251,249,0.98)_0%,rgba(248,251,249,0.9)_78%,rgba(248,251,249,0.72)_100%)] pb-4 backdrop-blur-xl dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(12,18,16,0.98)_0%,rgba(12,18,16,0.9)_78%,rgba(12,18,16,0.72)_100%)]',
        isKioskOwner ? '-mx-4 -mt-4 px-4 pt-4 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6' : '-mx-6 -mt-6 px-6 pt-6',
      )}>
        {/* Page Header */}
        <div className="mb-4" data-tour="page-header" data-tour-path={pathname}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
              {subtitle && <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{subtitle}</p>}
            </div>
            
            {actions && <div className="sm:mt-0">{actions}</div>}
          </div>
        </div>
        
        {/* Filters Section */}
        {filters && (
          <div>
            {isKioskOwner && (
              <button
                type="button"
                aria-expanded={mobileFiltersOpen}
                aria-controls="owner-mobile-filter-panel"
                onClick={() => setMobileFiltersOpen((open) => !open)}
                className="mb-3 inline-flex min-h-11 w-full items-center justify-between rounded-xl border border-gray-200 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-white dark:border-white/10 dark:bg-slate-900/70 dark:text-gray-200 dark:hover:bg-slate-800 md:hidden"
              >
                <span className="inline-flex items-center gap-2">
                  <FaFilter aria-hidden="true" className="text-primary-100" />
                  Filters
                </span>
                <span className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {mobileFiltersOpen ? 'Hide' : 'Show'}
                  <HiChevronDown
                    aria-hidden="true"
                    className={classNames('transition-transform', mobileFiltersOpen && 'rotate-180')}
                  />
                </span>
              </button>
            )}
            <div
              id={isKioskOwner ? 'owner-mobile-filter-panel' : undefined}
              className={isKioskOwner && !mobileFiltersOpen ? 'hidden md:block' : undefined}
            >
              {filters}
            </div>
          </div>
        )}
      </div>
      
      {/* Main Content */}
      <div className={classNames('bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-white/10 shadow-sm overflow-hidden', fillHeight && 'flex min-h-0 flex-1 flex-col')}>
        {children}
      </div>
      
      {/* Footer (Pagination, etc.) */}
      {footer && <div className="mt-6">{footer}</div>}
    </div>
  );
};

export default PageLayout;